import CommonDetailsDialog from "@/components/common/details/DetailsDialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Sheet } from "@/interface/dataInterface";
import { Inventory } from "@/interface/entityInterface";
import { formatDate } from "date-fns";
import {
  Building2,
  Package,
  DollarSign,
  Shield,
  Calendar,
  Hash,
  Boxes,
} from "lucide-react";
import { FC, useEffect } from "react";

export interface InventoryDetailsDialog extends Sheet {
  product: Inventory | null;
}

const InventoryDetails: FC<InventoryDetailsDialog> = ({
  open,
  setOpen,
  product,
}) => {
  if (!product) return;

  useEffect(() => {
    console.log(product)
  }, [product])
  return (
    <CommonDetailsDialog open={open} setOpen={setOpen} title={product.name}>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            #{product.id}
          </Badge>
          <Badge variant="outline" className="text-sm">
            SKU: {product.sku}
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Product Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-1">Description</h4>
              <p className="text-gray-600">{product.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Boxes className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-medium">{product.category}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Supplier ID</p>
                  <p className="font-medium">{product.supplier.id}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5" />
                Inventory
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Current Stock</p>
                <p className="text-2xl font-bold">
                  {product.quantity} {product.unit}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Min Stock Level</p>
                <p className="font-medium">
                  {product.min_stock_level ? `${product.min_stock_level} ${product.unit}` : "Not set"}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Unit Price</p>
                <p className="text-2xl font-bold">${product.price}</p>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Warranty</p>
                  <p className="font-medium">
                    {product.warranty_months} months
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">
                {formatDate(product.createdAt, "dd MMMM yyyy HH:mm")}
              </p>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-medium">
                {formatDate(product.updatedAt, "dd MMMM yyyy HH:mm")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </CommonDetailsDialog>
  );
};

export default InventoryDetails;
